'use client';

import { useRef, useState } from 'react';

import type { HomeWorkflowDiagram, HomeWorkflowTab } from '../../_content/home';

/** Chevron used in tab-panel headers (mobile accordion). */
function Chevron() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <polyline points="4 6 8 10 12 6" />
    </svg>
  );
}

function DownArrow() {
  return (
    <span className="tflow-arrow">
      <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M8 2v12" />
        <path d="M3 9l5 5 5-5" />
      </svg>
    </span>
  );
}

function Check() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
    </svg>
  );
}

/** "Publish the listing" flow diagram (decorative, aria-hidden in parent). */
function FlowDiagram({
  diagram,
}: {
  diagram: Extract<HomeWorkflowDiagram, { kind: 'flow' }>;
}) {
  const step0 = diagram.steps[0];
  const step1 = diagram.steps[1];
  const step2 = diagram.steps[2];

  return (
    <div className="tdiagram tflow">
      <div className="tflow-step">
        <span className="tflow-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 11l9-7 9 7" />
            <path d="M5 10v10h14V10" />
            <path d="M10 20v-5h4v5" />
          </svg>
        </span>
        <span className="tflow-text">
          <strong>{step0?.strong}</strong>
          <span>{step0?.sub}</span>
        </span>
      </div>
      <DownArrow />
      <div className="tflow-step">
        <span className="tflow-icon">
          <DocIcon />
        </span>
        <span className="tflow-text">
          <strong>{step1?.strong}</strong>
          <span>{step1?.sub}</span>
        </span>
      </div>
      <DownArrow />
      <div className="tflow-step">
        <span className="tflow-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18" />
            <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" />
          </svg>
        </span>
        <span className="tflow-text">
          <strong>{step2?.strong}</strong>
        </span>
        <span className="tflow-portals">
          <span className="tflow-portal">IS24</span>
          <span className="tflow-portal">Kleinanzeigen</span>
          <span className="tflow-portal tflow-portal--live">
            ● {diagram.portalsLabel.live}
          </span>
        </span>
      </div>
    </div>
  );
}

/** "Filter requests" criteria/applicants diagram. */
function FilterDiagram({
  diagram,
}: {
  diagram: Extract<HomeWorkflowDiagram, { kind: 'filter' }>;
}) {
  return (
    <div className="tdiagram tfilter">
      <div className="tfilter-col">
        <h4>{diagram.criteriaHeading}</h4>
        <ul className="tfilter-criteria">
          {diagram.criteria.map((c) => (
            <li key={c}>
              <span className="tfilter-check">
                <Check />
              </span>
              {c}
            </li>
          ))}
        </ul>
      </div>
      <div className="tfilter-col">
        <h4>{diagram.incomingHeading}</h4>
        <div className="tfilter-applicants">
          {diagram.applicants.map((a) => (
            <div
              key={a.id}
              className={`tfilter-applicant ${
                a.pass
                  ? 'tfilter-applicant--pass'
                  : 'tfilter-applicant--filtered'
              }`}
            >
              <strong>{a.id}</strong>
              <span
                className={`tfilter-status ${
                  a.pass ? 'tfilter-status--pass' : 'tfilter-status--off'
                }`}
              >
                {a.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** "Check documents" completeness diagram. */
function DocsDiagram({
  diagram,
}: {
  diagram: Extract<HomeWorkflowDiagram, { kind: 'docs' }>;
}) {
  return (
    <div className="tdiagram tdocs">
      <span className="tdiagram-pill">{diagram.pill}</span>
      {diagram.docs.map((d) => (
        <div className="tdoc" key={d.name}>
          <span className="tdoc-icon">
            <DocIcon />
          </span>
          <div className="tdoc-body">
            <p className="tdoc-name">{d.name}</p>
            <div className="tdoc-bar">
              <i style={{ width: '100%' }} />
            </div>
          </div>
          <span className="tdoc-badge tdoc-badge--ok">{d.badge}</span>
        </div>
      ))}
      <div className="tdoc tdoc--missing">
        <span className="tdoc-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 2-2.5 2-2.5 4" />
            <path d="M12 17h.01" />
          </svg>
        </span>
        <div className="tdoc-body">
          <p className="tdoc-name">{diagram.missing.name}</p>
          <div className="tdoc-bar">
            <i style={{ width: '25%' }} />
          </div>
        </div>
        <span className="tdoc-badge tdoc-badge--pending">
          {diagram.missing.badge}
        </span>
      </div>
      <div className="tdocs-footer">
        <span>{diagram.incomeLine}</span>
        <span>{diagram.docsCount}</span>
      </div>
    </div>
  );
}

function Diagram({ diagram }: { diagram: HomeWorkflowDiagram }) {
  switch (diagram.kind) {
    case 'flow':
      return <FlowDiagram diagram={diagram} />;
    case 'filter':
      return <FilterDiagram diagram={diagram} />;
    case 'docs':
      return <DocsDiagram diagram={diagram} />;
  }
}

/**
 * Home workflow section – desktop tabs + mobile accordion (prototype §6).
 * `is-active` drives the desktop tab selection, `is-open` the mobile accordion;
 * the CSS shows the relevant one per breakpoint. Content (tab labels, panel
 * copy, diagrams) is locale-driven and injected via the `tabs` prop – this
 * component holds no copy of its own.
 */
export function WorkflowTabs({
  tabs,
  ariaLabel,
}: {
  tabs: HomeWorkflowTab[];
  ariaLabel: string;
}) {
  const [active, setActive] = useState(0);
  // Mobile-Accordion: standardmäßig ALLE collapsed (null), damit man zuerst die
  // drei Haupt-Schritte als Überblick sieht. Desktop nutzt `is-active` (Tabs),
  // bleibt davon unberührt.
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const triggerRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const onKey = (e: React.KeyboardEvent, i: number) => {
    if (e.key === 'ArrowRight') {
      const n = (i + 1) % tabs.length;
      setActive(n);
      triggerRefs.current[n]?.focus();
    }
    if (e.key === 'ArrowLeft') {
      const p = (i - 1 + tabs.length) % tabs.length;
      setActive(p);
      triggerRefs.current[p]?.focus();
    }
  };

  const panelClass = (i: number) =>
    `tab-panel${i === active ? ' is-active' : ''}${i === openIdx ? ' is-open' : ''}`;

  const toggleAccordion = (i: number) =>
    setOpenIdx((cur) => (cur === i ? null : i));

  return (
    <>
      <div className="tabs reveal" role="tablist" aria-label={ariaLabel}>
        {tabs.map((tab, i) => (
          <button
            key={i}
            ref={(el) => {
              triggerRefs.current[i] = el;
            }}
            className="tab-trigger"
            role="tab"
            aria-selected={i === active}
            aria-controls={`panel-${i + 1}`}
            id={`tab-${i + 1}`}
            data-tab={i + 1}
            onClick={() => setActive(i)}
            onKeyDown={(e) => onKey(e, i)}
          >
            <span className="tab-num">{i + 1}</span>
            <span>{tab.tabLabel}</span>
          </button>
        ))}
      </div>

      <div className="tab-panels">
        {tabs.map((tab, i) => (
          <div
            key={i}
            className={panelClass(i)}
            role="tabpanel"
            id={`panel-${i + 1}`}
            aria-labelledby={`tab-${i + 1}`}
            data-panel={i + 1}
          >
            <button
              className="tab-panel-header"
              data-acc
              onClick={() => toggleAccordion(i)}
            >
              <span>
                <strong>{tab.accordionStrong}</strong>
                {tab.accordionRest}
              </span>
              <Chevron />
            </button>
            <div className="tab-panel-body">
              <div>
                <h3 className="h-section">{tab.panelTitle}</h3>
                {tab.panelParagraphs.map((p, pi) => (
                  <p
                    className="body-text"
                    key={pi}
                    style={pi > 0 ? { marginTop: 'var(--space-3)' } : undefined}
                  >
                    {p}
                  </p>
                ))}
              </div>
              <div className="tab-figure" aria-hidden="true">
                <Diagram diagram={tab.diagram} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
